import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Circle,
  ExternalLink,
  HelpCircle,
  LogOut,
  Monitor,
  Moon,
  RefreshCw,
  Sun,
} from 'lucide-react';
import packageJson from '../../../package.json';
import PageHeader from '../../components/layout/PageHeader';
import DatabaseToolsCard from '../../components/settings/DatabaseToolsCard';
import InstallAppButton from '../../components/pwa/InstallAppButton';
import { usePwaInstall } from '../../hooks/usePwaInstall';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { BANCO_DAUNIA_CONTACTS } from '../../lib/bancoContacts';
import { clearAppCache } from '../../lib/clearAppCache';
import { toast } from '../../lib/toast';
import { cn } from '../../lib/utils';

const themeOptions = [
  { value: 'light', label: 'Chiaro', icon: Sun },
  { value: 'white', label: 'Bianco', icon: Circle },
  { value: 'dark', label: 'Scuro', icon: Moon },
  { value: 'system', label: 'Sistema', icon: Monitor },
];

function SettingsRow({ label, description, children }) {
  return (
    <div className="flex flex-col gap-3 border-b border-[var(--ba-surface-border)] py-4 last:border-b-0 last:pb-0 first:pt-0 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-[var(--ba-body-text)]">{label}</p>
        {description && <p className="mt-0.5 text-sm ba-muted">{description}</p>}
      </div>
      <div className="shrink-0">{children}</div>
    </div>
  );
}

function PwaInstallRow() {
  const { installState, isInstalled } = usePwaInstall();

  if (isInstalled) {
    return (
      <Badge variant="secondary" className="bg-brand-green/15 text-brand-green">
        App installata
      </Badge>
    );
  }

  if (installState === 'unavailable') {
    return (
      <p className="max-w-xs text-right text-sm ba-muted">
        Non disponibile su questo browser. Su desktop usa Chrome o Edge; su iPhone usa Safari.
      </p>
    );
  }

  return <InstallAppButton size="default" />;
}

function SettingsPage() {
  const { themePreference, setThemePreference } = useTheme();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [cacheStatus, setCacheStatus] = useState('idle');

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const handleClearCache = async () => {
    setCacheStatus('loading');
    try {
      await clearAppCache();
      toast.success('Cache svuotata', 'L\'app userà i contenuti più recenti.');
    } catch {
      toast.error('Errore', 'Impossibile svuotare la cache.');
    } finally {
      setCacheStatus('idle');
    }
  };

  const cacheLabel = cacheStatus === 'loading' ? 'Pulizia…' : 'Svuota cache';

  return (
    <div className="mx-auto max-w-2xl px-4 py-8 md:py-12">
      <PageHeader
        title="Impostazioni"
        description="Personalizza l'app, gestisci l'installazione e l'accesso."
        backTo="/"
        backLabel="Torna alla home"
      />

      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Aspetto</CardTitle>
          </CardHeader>
          <CardContent>
            <SettingsRow
              label="Tema"
              description="Scegli l'aspetto dell'interfaccia. «Bianco» è neutro senza toni caldi; «Sistema» segue il dispositivo (chiaro o scuro)."
            >
              <div className="flex flex-wrap gap-2" role="group" aria-label="Tema">
                {themeOptions.map(({ value, label, icon: Icon }) => {
                  const isActive = themePreference === value;
                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setThemePreference(value)}
                      className={cn(
                        'inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold transition-colors',
                        isActive
                          ? 'border-brand-orange bg-brand-orange/10 text-brand-orange'
                          : 'border-[var(--ba-surface-border)] text-[var(--ba-body-text)] hover:bg-[var(--ba-hover-subtle)]'
                      )}
                      aria-pressed={isActive}
                    >
                      <Icon className="h-4 w-4" />
                      {label}
                    </button>
                  );
                })}
              </div>
            </SettingsRow>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">App</CardTitle>
          </CardHeader>
          <CardContent className="space-y-0">
            <SettingsRow
              label="Installa su dispositivo"
              description="Aggiungi l'app alla schermata Home o al desktop per un accesso rapido."
            >
              <PwaInstallRow />
            </SettingsRow>
            <SettingsRow
              label="Versione"
              description="Release attualmente in uso."
            >
              <span className="text-sm font-semibold tabular-nums text-[var(--ba-body-text)]">
                v{packageJson.version}
              </span>
            </SettingsRow>
            <SettingsRow
              label="Cache offline"
              description="Utile dopo un aggiornamento se vedi contenuti non aggiornati."
            >
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleClearCache}
                disabled={cacheStatus === 'loading'}
              >
                <RefreshCw className={cn('h-4 w-4', cacheStatus === 'loading' && 'animate-spin')} />
                {cacheLabel}
              </Button>
            </SettingsRow>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Aiuto e contatti</CardTitle>
          </CardHeader>
          <CardContent className="space-y-0">
            <SettingsRow
              label="Domande frequenti"
              description="Guida rapida su inviti, risposte ed eventi."
            >
              <Button asChild variant="outline" size="sm">
                <Link to="/#faq">
                  <HelpCircle className="h-4 w-4" />
                  Apri FAQ
                </Link>
              </Button>
            </SettingsRow>
            <SettingsRow
              label="Banco Alimentare della Daunia"
              description={BANCO_DAUNIA_CONTACTS.email}
            >
              <Button asChild variant="outline" size="sm">
                <a href={BANCO_DAUNIA_CONTACTS.website} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="h-4 w-4" />
                  Sito ufficiale
                </a>
              </Button>
            </SettingsRow>
          </CardContent>
        </Card>

        <DatabaseToolsCard />

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Account</CardTitle>
          </CardHeader>
          <CardContent>
            <SettingsRow
              label="Esci"
              description="Chiudi la sessione su questo dispositivo."
            >
              <Button type="button" variant="destructive" size="sm" onClick={handleLogout}>
                <LogOut className="h-4 w-4" />
                Logout
              </Button>
            </SettingsRow>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default SettingsPage;
